/* Premieres: records the station plays before they come out.
 *
 * WHAT THIS SECTION IS FOR, because it decides the whole layout. Editorially it
 * is what a label radio does — Flytree Records runs the station, and debuting other
 * people's records before release is the oldest reason a label ever had a radio
 * show. Structurally it is the cleanest thing this station broadcasts: under D14
 * we hold no collective licence, so every recording needs individual clearance,
 * and an unreleased recording is the one case where a direct grant from the
 * artist is fully effective rather than beside the point (types.ts has the
 * reasoning and the articles). That is why the feed leads with the release date
 * and why "unreleased only" is a first-class filter rather than a sort option.
 *
 * THE FILTER LIVES IN THE URL, like every filter on this site. `?unreleased=1`
 * and `?genre=` are read with useSearchParams and written back by navigating, so
 * a filtered feed is a thing you can send someone and the back button undoes a
 * filter instead of leaving the page.
 *
 * PRESSING PLAY ON A CARD LOADS THE PERSISTENT PLAYER. A card carries no
 * `audio_url` — that is on the detail — so play fetches the one premiere and
 * hands it to the player that lives above the router. There is no <audio>
 * element on this page and there must never be one: it would die the moment
 * somebody clicked through to the record.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { api, isAbortError } from '../api';
import { Chip, ChipList, Empty, ErrorNote, PremiereCard, Section, Spinner, useNarrow } from '../components';
import { readTzMode } from '../format';
import { usePlayer } from '../player';
import { premiereAsPlayable, premiereCatalogueNo } from '../types';
import type { PremiereCard as PremiereData } from '../types';

/** The API's own default page size, and the archive's. */
const PAGE_SIZE = 24;

/* ---------------------------------------------------------------- filters */

interface Filters {
  unreleased: boolean;
  genre: string | null;
}

function readFilters(params: URLSearchParams): Filters {
  return {
    unreleased: params.get('unreleased') === '1',
    genre: (params.get('genre') ?? '').trim().toLowerCase() || null,
  };
}

function toPath(filters: Filters): string {
  const params = new URLSearchParams();
  if (filters.unreleased) params.set('unreleased', '1');
  if (filters.genre) params.set('genre', filters.genre);
  const encoded = params.toString();
  return encoded ? `/premieres?${encoded}` : '/premieres';
}

/* ----------------------------------------------------------------- styles */

const HEAD: CSSProperties = {
  display: 'block',
  fontSize: 'clamp(2.75rem, 8vw, 4.75rem)',
  lineHeight: 0.92,
  letterSpacing: '-.015em',
  fontVariationSettings: "'wdth' 118, 'wght' 800",
};

const INTRO: CSSProperties = {
  maxWidth: '52ch',
  fontSize: '0.9375rem',
  color: 'var(--muted)',
};

const FROW: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '14px',
  padding: '14px clamp(12px, 4vw, 28px)',
  borderBottom: 'var(--bw) solid var(--edge)',
  flexWrap: 'wrap',
};

const FLABEL: CSSProperties = {
  width: '96px',
  flexShrink: 0,
  color: 'var(--muted)',
  paddingTop: '9px',
};

const NOTE: CSSProperties = {
  margin: 0,
  padding: '12px clamp(12px, 4vw, 28px)',
  lineHeight: 1.6,
};

const TAIL: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '20px',
  flexWrap: 'wrap',
  padding: '30px clamp(12px, 4vw, 28px) 40px',
};

function grid(columns: number): CSSProperties {
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    gap: 0,
    padding: '26px clamp(12px, 4vw, 28px) 0',
  };
}

/* ------------------------------------------------------------------- page */

type ListState = 'loading' | 'ready' | 'error';

export default function Premieres() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tz] = useState(readTzMode);

  /* Two breakpoints, both in JavaScript because these cards are inline-styled:
     one column of landscape cards below 1080, and below 560 the sleeve goes
     above the words rather than shrinking to a stamp. */
  const oneColumn = useNarrow(1080);
  const stacked = useNarrow(560);

  const filters = useMemo(() => readFilters(searchParams), [searchParams]);

  const [items, setItems] = useState<PremiereData[]>([]);
  const [total, setTotal] = useState(0);
  const [state, setState] = useState<ListState>('loading');
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  const [moreLoading, setMoreLoading] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null);
  const moreRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    // A filter change invalidates whatever "load more" was fetching for the
    // previous filters; appending that page would mix two result sets.
    moreRef.current?.abort();
    moreRef.current = null;
    setMoreLoading(false);
    setMoreError(null);

    setState('loading');
    setError(null);

    api
      .getPremieres(
        {
          genre: filters.genre ?? undefined,
          unreleased: filters.unreleased ? true : undefined,
          limit: PAGE_SIZE,
          offset: 0,
        },
        controller.signal,
      )
      .then((page) => {
        setItems(page.results);
        setTotal(page.total);
        setState('ready');
      })
      .catch((failure: unknown) => {
        if (isAbortError(failure)) return;
        setError(failure);
        setState('error');
      });

    return () => controller.abort();
  }, [filters, attempt]);

  useEffect(() => () => moreRef.current?.abort(), []);

  const loadMore = useCallback(() => {
    moreRef.current?.abort();
    const controller = new AbortController();
    moreRef.current = controller;
    setMoreLoading(true);
    setMoreError(null);

    api
      .getPremieres(
        {
          genre: filters.genre ?? undefined,
          unreleased: filters.unreleased ? true : undefined,
          limit: PAGE_SIZE,
          offset: items.length,
        },
        controller.signal,
      )
      .then((page) => {
        setTotal(page.total);
        setItems((current) => {
          const seen = new Set(current.map((premiere) => premiere.slug));
          return [...current, ...page.results.filter((one) => !seen.has(one.slug))];
        });
        setMoreLoading(false);
      })
      .catch((failure: unknown) => {
        if (isAbortError(failure)) return;
        setMoreError(failure);
        setMoreLoading(false);
      });
  }, [filters, items.length]);

  useEffect(() => {
    const scope = filters.unreleased ? 'Unreleased — ' : '';
    document.title = `${scope}Premieres — Higher Ground Radio`;
  }, [filters.unreleased]);

  /* -------------------------------------------------------------- player */

  const player = usePlayer();
  const playerRef = useRef(player);
  playerRef.current = player;

  /** The premiere this page handed to the player. Null when it was not us. */
  const [loadedSlug, setLoadedSlug] = useState<string | null>(null);
  const [startingSlug, setStartingSlug] = useState<string | null>(null);
  const [playNote, setPlayNote] = useState<string | null>(null);
  const startRef = useRef<AbortController | null>(null);

  useEffect(() => () => startRef.current?.abort(), []);

  const play = useCallback(
    (card: PremiereData) => {
      const current = playerRef.current;
      const catalogue = premiereCatalogueNo(card);

      // Already loaded: this is a pause or a resume, not a second fetch.
      if (
        loadedSlug === card.slug &&
        current.mode === 'episode' &&
        current.current?.catalogue_no === catalogue
      ) {
        current.toggle();
        return;
      }

      startRef.current?.abort();
      const controller = new AbortController();
      startRef.current = controller;
      setPlayNote(null);
      setStartingSlug(card.slug);

      api
        .getPremiere(card.slug, controller.signal)
        .then((detail) => {
          setStartingSlug(null);
          /* No file on the server. For a premiere that is almost always because
             it has not aired yet — the audio goes up when it goes out — so the
             note says which of the two it is rather than reading as a fault. */
          if (!detail.audio_url) {
            setPlayNote(
              `We do not have a file for ${detail.track_title} yet. Open it for the release date and the buy link.`,
            );
            return;
          }
          setLoadedSlug(detail.slug);
          playerRef.current.playEpisode(premiereAsPlayable(detail));
        })
        .catch((failure: unknown) => {
          if (isAbortError(failure)) return;
          setStartingSlug(null);
          setPlayNote(`We could not load ${card.track_title}. Try again in a moment.`);
        });
    },
    [loadedSlug],
  );

  /* The card is drawn as playing only if the player is still on the premiere we
     handed it: starting an archive set from anywhere else takes the highlight
     off this page, which is the truth of a station with one audio element. */
  const isLoaded = useCallback(
    (card: PremiereData) =>
      loadedSlug === card.slug &&
      player.mode === 'episode' &&
      player.current?.catalogue_no === premiereCatalogueNo(card) &&
      player.isPlaying,
    [loadedSlug, player],
  );

  /* ---------------------------------------------------------------- view */

  const apply = useCallback(
    (next: Filters) => {
      navigate(toPath(next));
    },
    [navigate],
  );

  const remaining = Math.max(0, total - items.length);
  const countWord = total === 1 ? '1 premiere' : `${total} premieres`;
  const filtered = filters.unreleased || filters.genre !== null;

  return (
    <>
      <Section
        level={1}
        title={<span style={HEAD}>Premieres</span>}
        meta={state === 'loading' ? 'Counting…' : state === 'error' ? 'Unavailable' : countWord}
      >
        <p className="t-body" style={INTRO}>
          Records we put on air before they come out: one track, one artist, one release date. An
          unreleased recording is the one thing we can broadcast on the artist&rsquo;s own say-so and
          nothing else, which makes this the part of the schedule we can say yes to fastest. Every
          link under a premiere goes to the people who made it, not to us.
        </p>
      </Section>

      <div style={FROW}>
        <div className="mono" style={FLABEL}>
          Show
        </div>
        {/* Two chips rather than one toggle. A single "unreleased" switch has an
            off state nobody can see; two named options say which view is on. */}
        <ChipList label="Show">
          <Chip
            selected={!filters.unreleased}
            onPress={() => apply({ ...filters, unreleased: false })}
          >
            Everything
          </Chip>
          <Chip
            selected={filters.unreleased}
            onPress={() => apply({ ...filters, unreleased: true })}
          >
            Unreleased only
          </Chip>
          {/* A genre arrives here from a card's chip. Shown as an active filter
              that can be taken off again, the same way the archive treats `?q=`. */}
          {filters.genre ? (
            <Chip
              selected
              ariaLabel={`Remove the ${filters.genre} filter`}
              onPress={() => apply({ ...filters, genre: null })}
            >
              {filters.genre} ×
            </Chip>
          ) : null}
        </ChipList>
      </div>

      {playNote ? (
        <p className="mono dim" role="status" style={NOTE}>
          {playNote}
        </p>
      ) : null}

      {state === 'error' ? (
        <ErrorNote
          error={error}
          what="the premieres"
          onRetry={() => setAttempt((n) => n + 1)}
        />
      ) : state === 'loading' ? (
        <div style={{ padding: '34px clamp(12px, 4vw, 28px)' }}>
          <Spinner label="Loading the premieres" />
        </div>
      ) : items.length === 0 ? (
        <Empty
          kind={filtered ? 'No matches' : 'Nothing yet'}
          title={
            filters.genre
              ? `Nothing in ${filters.genre} has premiered here yet.`
              : filters.unreleased
                ? 'Every premiere we are holding is out already.'
                : 'No records have premiered here yet.'
          }
          actions={
            <>
              {filtered ? (
                <Link className="btn" to="/premieres">
                  Show everything
                </Link>
              ) : null}
              <Link className={filtered ? 'btn-ghost' : 'btn'} to="/submit">
                Send us a record
              </Link>
              <Link className="btn-ghost" to="/archive">
                Browse the archive
              </Link>
            </>
          }
        >
          <p className="t-body">
            {filtered
              ? 'A premiere leaves this list the day it comes out — the record is still on the site, it is just no longer unreleased. Take the filter off and it is there.'
              : 'A premiere is one unreleased track, played here first, with the release date and a link to buy it. If you have a record coming out and you want it heard before it does, the open call is the way in.'}
          </p>
        </Empty>
      ) : (
        <>
          <div style={grid(oneColumn ? 1 : 2)}>
            {items.map((premiere) => (
              <PremiereCard
                key={premiere.slug}
                premiere={premiere}
                tz={tz}
                stacked={stacked}
                onPlay={play}
                isPlaying={isLoaded(premiere)}
                isStarting={startingSlug === premiere.slug}
                genreHref={(slug) => `/premieres?genre=${encodeURIComponent(slug)}`}
              />
            ))}
          </div>

          {moreError ? (
            <ErrorNote error={moreError} what="the next page of premieres" onRetry={loadMore} />
          ) : null}

          <div style={TAIL}>
            {remaining > 0 ? (
              <button
                type="button"
                className="btn-ghost"
                style={{ padding: '16px 28px', fontSize: '1rem' }}
                disabled={moreLoading}
                onClick={loadMore}
              >
                {moreLoading ? 'Loading…' : `Load ${Math.min(PAGE_SIZE, remaining)} more`}
              </button>
            ) : null}
            <span className="mono dim">
              Showing {items.length} of {total} · newest first
            </span>
          </div>
        </>
      )}
    </>
  );
}
